import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Home,
  Heart,
  Building2,
  Users,
  MessageSquare,
  BarChart3,
  UserPlus,
  Briefcase,
  LogOut,
  Settings,
  Shield,
  UserCheck,
  Calendar,
  Menu,
  X,
  TrendingUp,
  Wallet,
  UserCog,
  Banknote,
  FolderKanban,
  GitBranch,
  Receipt,
  Wrench,
  CalendarDays,
  BookOpen,
  MapPin,
}from "lucide-react";

// Dashboard section components
import DashboardOverview from "../components/dashboard/DashboardOverview";
import MyProperties from "../components/dashboard/MyProperties";
import CRMLeads from "../components/dashboard/CRMLeads";
import CRMDeals from "../components/dashboard/CRMDeals";
import Inquiries from "../components/dashboard/Inquiries";
import AgentManagement from "../components/dashboard/AgentManagement";
import AccessControl from "../components/dashboard/AccessControl";
import TourBookings from "../components/dashboard/TourBookings";
import UserRoleManager from "../components/dashboard/UserRoleManager";
import AdminAgentApplications from "../components/dashboard/AdminAgentApplications";
import AdminAllProperties from "../components/dashboard/AdminAllProperties";
import AdminAllLeads from "../components/dashboard/AdminAllLeads";
import AdminAllDeals from "../components/dashboard/AdminAllDeals";
import BusinessSettings from "../components/dashboard/BusinessSettings";
import RentalManagement from "../components/dashboard/RentalManagement";
import PropertyAnalytics from "../components/dashboard/PropertyAnalytics";
import FinanceDashboard from "../components/dashboard/FinanceDashboard";
import AgentPerformanceView from "../components/dashboard/AgentPerformanceView";
import PayrollDashboard from "../components/dashboard/PayrollDashboard";
import ProjectManagement from "../components/dashboard/ProjectManagement";
import BranchManagement from "../components/dashboard/BranchManagement";
import PettyCashManagement from "../components/dashboard/PettyCashManagement";
import TeamDashboard from "../components/dashboard/TeamDashboard";
import ExecutiveDashboard from "../components/dashboard/ExecutiveDashboard";
import MaintenanceManagement from "../components/dashboard/MaintenanceManagement";
import LeaveManagement from "../components/dashboard/LeaveManagement";
import AccountingDashboard from "../components/dashboard/AccountingDashboard";
import BranchManagerDashboard from "../components/dashboard/BranchManagerDashboard";
import BranchAgents from "../components/dashboard/BranchAgents";
import BranchReport from "../components/dashboard/BranchReport";
import RegionalDashboard from "../components/dashboard/RegionalDashboard";
import RegionalReport from "../components/dashboard/RegionalReport";
import RegionsManagement from "../components/dashboard/RegionsManagement";
import BrokerDashboard from "../components/dashboard/BrokerDashboard";
import BrokerWallet from "../components/dashboard/BrokerWallet";
import AdminBrokerApplications from "../components/dashboard/AdminBrokerApplications";
import { getSidebarForRole, COMMON_ITEMS } from "../config/sidebarConfig";

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState("/logo.png");

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/business`);
        if (response.data?.logo_url) {
          setLogoUrl(response.data.logo_url);
        }
      } catch (error) {
        // Keep default logo on error
      }
    };
    fetchSettings();
  }, []);

  const isAgent = user?.role === "agent" || user?.role === "admin";
  const isAdmin = user?.role === "admin";
  const isCEO = user?.role === "ceo";
  // Role-specific sidebar config (single source of truth, see config/sidebarConfig.js)
  const sidebarCfg = getSidebarForRole(user?.role);
  const hasNoDedicatedSidebar = user && !sidebarCfg;

  const renderRoleSidebar = (onItemClick = null) => {
    if (!sidebarCfg) return null;
    return (
      <>
        {renderNavItems(sidebarCfg.items, sidebarCfg.title, !!sidebarCfg.highlight, onItemClick)}
        {sidebarCfg.extraItems && renderNavItems(sidebarCfg.extraItems, sidebarCfg.extraTitle || "Tools", false, onItemClick)}
      </>
    );
  };

  const navItems = COMMON_ITEMS;

  const renderNavItems = (items, sectionTitle, isAdminSection = false, onItemClick = null) => (
    <>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 px-3 pt-4 first:pt-0">
        {sectionTitle}
      </p>
      {items.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          onClick={onItemClick}
          className={`flex items-center gap-3 px-3 py-3 rounded-sm text-sm transition-colors touch-manipulation ${
            (item.path === "/dashboard" ? location.pathname === "/dashboard" : location.pathname.startsWith(item.path))
              ? isAdminSection ? "bg-destructive text-destructive-foreground" : "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted hover:text-foreground active:bg-muted/80"
          }`}
          style={{ minHeight: '44px' }}
        >
          <item.icon className="w-5 h-5" />
          {item.label}
        </Link>
      ))}
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Top Bar */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-50">
        <div className="flex items-center justify-between h-full px-3 sm:px-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-3 -ml-2 rounded-sm hover:bg-muted active:bg-muted/80 transition-colors touch-manipulation"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              style={{ minWidth: '44px', minHeight: '44px' }}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            
            <Link to="/" className="flex items-center gap-2">
              <img src={logoUrl} alt="Buyorent" className="h-7 sm:h-8 w-auto object-contain" />
              <span className="font-serif text-lg sm:text-xl font-semibold hidden sm:block">Buyorent</span>
            </Link>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/properties" className="text-sm text-muted-foreground hover:text-foreground hidden sm:block">
              Browse Properties
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2">
                  <img 
                    src={user?.picture || `https://ui-avatars.com/api/?name=${user?.name}`}
                    alt={user?.name}
                    className="w-8 h-8 rounded-full"
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 text-sm">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-muted-foreground text-xs">{user?.email}</p>
                </div>
                <DropdownMenuItem onClick={logout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`fixed left-0 top-16 bottom-0 w-[85vw] max-w-72 bg-card border-r border-border overflow-y-auto z-40 md:hidden transform transition-transform duration-200 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="mobile-sidebar"
      >
        <nav className="p-4 space-y-1">
          {renderNavItems(navItems, "Menu", false, () => setMobileMenuOpen(false))}
          {renderRoleSidebar(() => setMobileMenuOpen(false))}
          {hasNoDedicatedSidebar && (
            <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-sm">
              <h4 className="font-medium text-sm mb-2">Become an Agent</h4>
              <p className="text-xs text-muted-foreground mb-3">
                List properties and access CRM tools.
              </p>
              <Button size="sm" variant="outline" className="w-full" asChild>
                <Link to="/agents" onClick={() => setMobileMenuOpen(false)}>Learn More</Link>
              </Button>
            </div>
          )}
        </nav>
      </aside>

      <div className="flex pt-16">
        {/* Desktop Sidebar */}
        <aside className="fixed left-0 top-16 bottom-0 w-64 bg-card border-r border-border overflow-y-auto hidden md:block">
          <nav className="p-4 space-y-1">
            {renderNavItems(navItems, "Menu")}
            {renderRoleSidebar()}
            {hasNoDedicatedSidebar && (
              <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-sm">
                <h4 className="font-medium text-sm mb-2">Become an Agent</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  List properties and access CRM tools.
                </p>
                <Button size="sm" variant="outline" className="w-full" asChild>
                  <Link to="/agents">Learn More</Link>
                </Button>
              </div>
            )}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 md:ml-64 p-6">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="saved" element={<DashboardOverview />} />
            <Route path="properties" element={<MyProperties />} />
            <Route path="analytics" element={<PropertyAnalytics API_URL={process.env.REACT_APP_BACKEND_URL} viewAll={false} />} />
            <Route path="agents" element={<AgentManagement />} />
            <Route path="inquiries" element={<Inquiries />} />
            <Route path="tours" element={<TourBookings />} />
            <Route path="leads" element={<CRMLeads />} />
            <Route path="deals" element={<CRMDeals />} />
            <Route path="team" element={<TeamDashboard />} />
            <Route path="executive" element={<ExecutiveDashboard />} />
            <Route path="access-control" element={<AccessControl />} />
            <Route path="admin/users" element={<UserRoleManager />} />
            <Route path="admin/analytics" element={<PropertyAnalytics API_URL={process.env.REACT_APP_BACKEND_URL} viewAll={true} />} />
            <Route path="admin/agent-performance" element={<AgentPerformanceView />} />
            <Route path="admin/applications" element={<AdminAgentApplications />} />
            <Route path="admin/all-properties" element={<AdminAllProperties />} />
            <Route path="admin/all-leads" element={<AdminAllLeads />} />
            <Route path="admin/all-deals" element={<AdminAllDeals />} />
            <Route path="admin/rentals" element={<RentalManagement />} />
            <Route path="admin/projects" element={<ProjectManagement />} />
            <Route path="admin/branches" element={<BranchManagement />} />
            <Route path="admin/petty-cash" element={<PettyCashManagement />} />
            <Route path="admin/maintenance" element={<MaintenanceManagement />} />
            <Route path="maintenance" element={<MaintenanceManagement />} />
            <Route path="admin/leave" element={<LeaveManagement />} />
            <Route path="admin/finance" element={<FinanceDashboard />} />
            <Route path="admin/accounting" element={<AccountingDashboard />} />
            <Route path="admin/payroll" element={<PayrollDashboard />} />
            <Route path="admin/business-settings" element={<BusinessSettings />} />
            <Route path="branch" element={<BranchManagerDashboard />} />
            <Route path="branch/agents" element={<BranchAgents />} />
            <Route path="branch/report" element={<BranchReport />} />
            <Route path="regional" element={<RegionalDashboard />} />
            <Route path="regional/branches" element={<RegionalDashboard />} />
            <Route path="regional/report" element={<RegionalReport />} />
            <Route path="admin/regions" element={<RegionsManagement />} />
            <Route path="broker" element={<BrokerDashboard />} />
            <Route path="broker/wallet" element={<BrokerWallet />} />
            <Route path="admin/broker" element={<BrokerDashboard />} />
            <Route path="admin/broker-wallet" element={<BrokerWallet />} />
            <Route path="admin/broker-applications" element={<AdminBrokerApplications />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default DashboardPage;
